import { useEffect, useRef, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function OpenFlexDays() {
  const [groupCode, setGroupCode] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [resolvedCode, setResolvedCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const iframeLoadedRef = useRef(false);

  // iOS Safari fallback or iframe-load timeout
  useEffect(() => {
    if (!pdfUrl) return;
    iframeLoadedRef.current = false;
    if (isIOS()) {
      setUseFallback(true);
      return;
    }
    const timer = setTimeout(() => {
      if (!iframeLoadedRef.current) setUseFallback(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [pdfUrl]);

  async function lookup(e) {
    e?.preventDefault?.();
    setError("");
    setPdfUrl("");
    setUseFallback(false);

    const code = groupCode.toUpperCase().trim();
    if (!code) {
      setError("Please enter a group code.");
      return;
    }

    setLoading(true);
    try {
      const checkUrl = `${API_URL}/api/public/flex-days/${encodeURIComponent(code)}/check`;
      const resp = await fetch(checkUrl, { method: "GET" });

      if (resp.status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
        return;
      }
      if (!resp.ok) {
        setError("Something went wrong. Please try again or contact the office.");
        return;
      }
      const data = await resp.json();
      if (!data.exists) {
        setError(`Couldn't find a group matching "${code}". Try a group code like B01 or N02, or your group name (e.g. Lobsters). Office: 516-593-2267.`);
        return;
      }

      const resolved = data.group_code;
      const resolvedName = data.group_name ? `${resolved} ${data.group_name}` : resolved;
      const pdfUrlFinal = `${API_URL}/api/public/flex-days/${encodeURIComponent(resolved)}`;
      setResolvedCode(resolvedName);
      setPdfUrl(pdfUrlFinal);
    } catch {
      setError("Something went wrong. Please try again or contact the office.");
    } finally {
      setLoading(false);
    }
  }

  function lookupAnother() {
    setGroupCode("");
    setPdfUrl("");
    setResolvedCode("");
    setError("");
    setUseFallback(false);
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-6">
          <img
            src="/rr_logo.png"
            alt="Rolling River Day Camp"
            className="h-20 mx-auto mb-2"
            data-testid="public-logo"
          />
          <h1 className="text-2xl font-bold text-[#1a3a6b]">Rolling River Day Camp</h1>
        </header>

        {!pdfUrl && (
          <div className="bg-white rounded-lg shadow p-6" data-testid="lookup-form-card">
            <h2 className="text-xl font-semibold mb-2 text-slate-800">
              Look Up Your Flex Day Availability
            </h2>
            <p className="text-slate-600 mb-4">
              Enter your camper's group code to see available flex dates.
            </p>

            <form onSubmit={lookup}>
              <input
                type="text"
                value={groupCode}
                onChange={(e) => setGroupCode(e.target.value)}
                placeholder="Group Code (e.g., B2 or Lobsters)"
                className="w-full px-4 py-3 text-lg border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                data-testid="group-code-input"
                aria-label="Group code"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-3 px-4 py-3 bg-[#1a3a6b] text-white font-semibold rounded-md hover:opacity-90 disabled:opacity-50"
                data-testid="lookup-button"
              >
                {loading ? "Looking up..." : "Find My Group"}
              </button>
            </form>

            {error && (
              <div
                className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"
                data-testid="lookup-error"
              >
                {error}
              </div>
            )}

            <div className="mt-6 pt-6 border-t text-sm text-slate-600 text-center space-y-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-left">
                <p className="font-semibold text-amber-900 mb-1">To purchase a flex date:</p>
                <p className="text-amber-800">
                  Email{" "}
                  <a
                    href="mailto:marissa@rollingriver.com?subject=Flex%20Date%20Purchase%20Request"
                    className="font-medium underline hover:text-amber-900"
                    data-testid="email-marissa-link"
                  >
                    marissa@rollingriver.com
                  </a>{" "}
                  to receive approval before booking.
                </p>
              </div>
              <p>Don't know your group code?</p>
              <p>
                Contact the office at <strong>516-593-2267</strong>
              </p>
            </div>
          </div>
        )}

        {pdfUrl && (
          <div className="bg-white rounded-lg shadow p-4" data-testid="pdf-view-card">
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-slate-800">Group {resolvedCode}</h2>
              <p className="text-xs text-slate-500">Updated in real-time</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[#1a3a6b] text-white rounded-md text-sm font-medium hover:opacity-90"
                data-testid="download-pdf-button"
              >
                Download PDF
              </a>
              <button
                onClick={lookupAnother}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-300"
                data-testid="lookup-another-button"
              >
                Look up another group
              </button>
            </div>

            {useFallback ? (
              <div
                className="flex flex-col items-center justify-center text-center bg-slate-50 border border-slate-200 rounded-md py-12 px-4"
                data-testid="pdf-fallback"
              >
                <p className="text-slate-700 mb-4">Your device can't preview PDFs inline.</p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-[#1a3a6b] text-white rounded-md font-semibold text-lg"
                  data-testid="tap-to-view-pdf"
                >
                  Tap to View PDF
                </a>
              </div>
            ) : (
              <iframe
                src={pdfUrl}
                title="Flex Day Availability"
                className="w-full"
                style={{ height: "75vh", border: "1px solid #e2e8f0", borderRadius: "4px" }}
                onLoad={() => {
                  iframeLoadedRef.current = true;
                }}
                data-testid="pdf-iframe"
              />
            )}

            <div className="mt-4 pt-4 border-t text-sm text-slate-600 text-center space-y-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-left">
                <p className="font-semibold text-amber-900 mb-1">To purchase a flex date:</p>
                <p className="text-amber-800">
                  Email{" "}
                  <a
                    href="mailto:marissa@rollingriver.com?subject=Flex%20Date%20Purchase%20Request"
                    className="font-medium underline hover:text-amber-900"
                    data-testid="email-marissa-link-pdf"
                  >
                    marissa@rollingriver.com
                  </a>{" "}
                  to receive approval before booking.
                </p>
              </div>
              <p>
                Have questions? Contact the office at <strong>516-593-2267</strong>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
